import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Grid, Header, Icon, Label, Message, Segment, SemanticSIZES } from 'semantic-ui-react';
import { setVehicleOrderSeq } from '../../../../app/common/util/util';
import { PermitFormValues } from '../../../../app/models/permit';
import { useStore } from '../../../../app/stores/store';
import StepMenu from '../../steps/StepMenu';
import PermitFormStep4VehicleEditForm from './PermitFormStep4VehicleEditForm';
import PermitFormStep4VehicleAddForm from './PermitFormStep4VehicleAddForm';

interface Props {
    values: PermitFormValues,
    prev: () => void,
    next: () => void,
    goTo: (step: number) => void,
    handleChange: (name: string|any, value: string|object) => void,
    buttonSize: SemanticSIZES
}

export default observer(function PermitFormStep4({ 
    values,
    next,
    prev,
    goTo,
    handleChange,
    buttonSize
 }: Props) {
    const {modalStore} = useStore();
    const {openModal} = modalStore;
    const [addVehicleMode, setAddVehicleMode] = useState(false);

    function handlePrimaryVehicle(i: number) {
        // disable exist primary
        var existIndex = values.vehicles.findIndex(x => x.vehicleIsPrimary === true);
        values.vehicles[existIndex].vehicleIsPrimary = false;

        values.vehicles[i].vehicleIsPrimary = true;

        // sort again
        values.vehicles = setVehicleOrderSeq(values.vehicles, i); // i == primary vehicle index in array

        handleChange('vehicles', values.vehicles);
    }
    
    function handleDeleteVehicle(i: number) {
        values.vehicles.splice(i, 1);

        // set vehicle order seq when deleting
        var primaryIndex = values.vehicles.findIndex(x => x.vehicleIsPrimary);
        values.vehicles = setVehicleOrderSeq(values.vehicles, primaryIndex);

        handleChange('vehicles', values.vehicles);
    }

    return (
        <>
            <StepMenu currentTab={values.step} position='top' permitTypeId={values.permitTypeId}/>
            <Segment attached clearing>
                <Grid>
                    <Grid.Column width={16}>
                        <Header 
                            floated='left' 
                            as='h2' 
                            icon='car' 
                            content='Vehicle Information'
                        />
                        <Button 
                            floated='right' 
                            basic 
                            size={buttonSize}
                            content={addVehicleMode ? 'Cancel' : 'Add Vehicle'}
                            onClick={() => setAddVehicleMode(!addVehicleMode)}
                        />
                    </Grid.Column>
                    <Grid.Column width={16}>
                        {!addVehicleMode && values.vehicles.length === 0 && (
                            <Message icon color='yellow'>
                                <Icon name='exclamation' />
                                <Message.Content>
                                    <Message.Header>Requirement</Message.Header>
                                    Please add at least one vehicle.
                                </Message.Content>
                            </Message>
                        )}

                        {addVehicleMode ? (
                            <PermitFormStep4VehicleAddForm 
                                propValues={values} 
                                setAddVehicleMode={(status: boolean) => setAddVehicleMode(status)} 
                                buttonSize={buttonSize}
                            />
                        ) : (
                            <Card.Group itemsPerRow={5} stackable doubling>
                            {values.vehicles.map((v, i) => (
                                <Card key={`${v.id}`}
                                    className={v.vehicleIsPrimary ? 'border-green' : 'no-border'}>
                                    {v.vehicleIsPrimary ? (
                                        <Label 
                                            size='large' 
                                            as='a' 
                                            color='green' 
                                            attached='top'
                                        >
                                        <Icon name='star' />
                                        Primary
                                        </Label>
                                    ) : (
                                        <Label 
                                            size='large' 
                                            as='a' 
                                            onClick={() => handlePrimaryVehicle(i)} 
                                            color='orange' 
                                            attached='top'
                                        >
                                            <Icon name='star outline' className='outline-none' />
                                            Set Primary
                                        </Label>
                                    )}
                                    
                                    <Card.Content>
                                        <Card.Header>{`${v.year} ${v.make}`}</Card.Header>
                                        <Card.Meta style={{fontSize: '1.2em'}}>
                                            {v.model}
                                        </Card.Meta>
                                        <Card.Description style={{fontSize: '1.2em'}}>
                                            {v.licensePlateNbr}
                                        </Card.Description>
                                    </Card.Content>

                                    {values.permitTypeId === 3 &&
                                    <Card.Content extra>
                                        HOV Number: {v.hovTransitVehicleNbr}<br />
                                        Transit Agency: {v.transitAgency}
                                    </Card.Content>
                                    }
                                    
                                    <div className='ui two buttons'>
                                        <Button 
                                            basic 
                                            color='green'
                                            value={i}
                                            onClick={() => 
                                                openModal(<PermitFormStep4VehicleEditForm
                                                    propValues={values} 
                                                    vehicle={values.vehicles[i]}
                                                    handleChange={handleChange}
                                                    buttonSize={buttonSize}
                                                />)
                                            }
                                            size={buttonSize}
                                        >
                                            Edit
                                        </Button>

                                        <Button 
                                            basic 
                                            negative
                                            onClick={() => handleDeleteVehicle(i)}
                                            size={buttonSize}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                            </Card.Group>
                        )}
                    </Grid.Column>

                    <Grid.Column width={16}>
                        {!addVehicleMode && 
                            <>
                                <Button
                                    onClick={() => prev()}
                                    content='Back'
                                    style={{display: values.isFinalView ? 'none' : 'inline-block'}}
                                    size={buttonSize}
                                />

                                <Button
                                    disabled={values.vehicles.length === 0}
                                    floated='right'
                                    positive
                                    onClick={values.isFinalView
                                        ? () => goTo(6)
                                        : () => next()
                                    }
                                    content={values.isFinalView ? 'Final Review' : 'Next'}
                                    size={buttonSize}
                                />
                            </>
                        }
                    </Grid.Column>
                </Grid>
            </Segment>
        </>
    )
})